import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from '../../app.service';
import { UtilsService } from '../../shared/utils.service';
import { PrequalificationService } from '../../prequalifications/prequalification.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { tap } from 'rxjs/operators';
import { CustomDataSource } from '../../utils/custom-data-source';
import { ClientPreQualificationsViewComponent } from './client-pre-qualifications-view/client-pre-qualifications-view.component';
import { ClientPreQualificationsEditComponent } from './client-pre-qualifications-edit/client-pre-qualifications-edit.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterService } from '../../shared/router.service';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-client-pre-qualifications',
  templateUrl: './client-pre-qualifications.component.html',
  styleUrls: ['./client-pre-qualifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientPreQualificationsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource = new ClientPreQualificationsDataSource(this.app, this.api);
  displayed_columns = [
    'id',
    'name',
    'client',
    'score',
    'items',
    'date_created',
    'date_modified',
    'buttons'
  ];
  query: string;

  // RBAC permission flags: resolved once in ngOnInit().
  canView: boolean = false;
  canEdit: boolean = false;

  constructor(
    private app: AppService,
    private utils: UtilsService,
    public prequalService: PrequalificationService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    private routerService: RouterService,
    private route: ActivatedRoute,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CLIENT_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CLIENT_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canView && !this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access client pre-qualifications.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayed_columns dynamically.
    this.displayed_columns = [
      'id',
      'name',
      'client',
      'score',
      'items',
      'date_created',
      // 'date_created_UTC',
      'date_modified'
      // 'date_modified_UTC',
    ];
    if (this.canView || this.canEdit) {
      this.displayed_columns.push('buttons');
    }

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData(false);
    }

    this.handleParams(this.route.snapshot.queryParams);
  }

  handleParams(queryParams) {
    if (queryParams['view']) {
      this.view(queryParams['view']);
    } else if (queryParams['edit']) {
      this.edit(queryParams['edit']);
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC columns by their corresponding date columns.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      }

      if (this.sort.active == 'date_modified_UTC') {
        this.dataSource.sort_by = 'date_modified';
      }

      this.dataSource.getData(false);
    });
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  public view(id: number) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view client pre-qualifications.'
      );
      return;
    }
    this.routerService.setQueryParams({ view: id });
    this.dialog
      .open(ClientPreQualificationsViewComponent, {
        width: '900px',
        data: id
      })
      .afterClosed()
      .subscribe((success) => {
        this.routerService.setQueryParams({});
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }

  public edit(id: number) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit client pre-qualifications.'
      );
      return;
    }
    this.routerService.setQueryParams({ edit: id });
    this.dialog
      .open(ClientPreQualificationsEditComponent, {
        width: '900px',
        data: id
      })
      .afterClosed()
      .subscribe((success) => {
        this.routerService.setQueryParams({});
        if (!success) {
          return;
        }
        if (this.canView) {
          this.dataSource.getData(true);
        }
      });
  }
}

export class ClientPreQualificationsDataSource extends CustomDataSource {
  sort_by = 'name';
  sort_order = 'asc';
  pageSizeOptions = [5, 10, 15];

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI('my/prequalifications', resetOffset);
  }
}
